// ./src/components/features/Rentals/RentalAssetsFilterBar/RentalAssetsFilterBar.tsx
//
// Filter controls for the Rental-assets catalog: by category. Fully controlled —
// the page owns the value and maps it to a RentalAssetFilter.

import React from "react";
import { Group, Box, Stack, Button } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { AppIcon, CategorySelect } from "@/components/generic";

export interface RentalAssetsFilterValue {
  categoryId: string | null;
}

export interface RentalAssetsFilterBarProps {
  value: RentalAssetsFilterValue;
  onChange: (value: RentalAssetsFilterValue) => void;
  disabled?: boolean;
  showClear?: boolean;
  vertical?: boolean;
}

export const EMPTY_RENTAL_ASSETS_FILTER: RentalAssetsFilterValue = {
  categoryId: null,
};

const isFilterActive = (value: RentalAssetsFilterValue): boolean => Boolean(value.categoryId);

const CATEGORY_STYLE: React.CSSProperties = { flex: "1 1 220px", minWidth: 200 };

const RentalAssetsFilterBarComponent: React.FC<RentalAssetsFilterBarProps> = ({
  value,
  onChange,
  disabled = false,
  showClear = true,
  vertical = false,
}) => {
  const { t } = useTranslation(["rentals", "common"]);

  const active = isFilterActive(value);

  const categoryControl = (
    <Box style={vertical ? undefined : CATEGORY_STYLE}>
      <CategorySelect
        group="rental"
        label={t("rentals:filters.category.label", { defaultValue: "Category" })}
        placeholder={t("rentals:filters.category.placeholder", { defaultValue: "All categories" })}
        value={value.categoryId}
        onChange={(id) => onChange({ ...value, categoryId: id })}
        clearable
        disabled={disabled}
        size="md"
      />
    </Box>
  );

  if (vertical) {
    return <Stack gap="md">{categoryControl}</Stack>;
  }

  return (
    <Group gap="sm" align="flex-end" wrap="wrap">
      {categoryControl}

      {active && showClear && (
        <Button
          variant="subtle"
          color="gray"
          size="md"
          onClick={() => onChange(EMPTY_RENTAL_ASSETS_FILTER)}
          disabled={disabled}
          leftSection={<AppIcon iconName="X" size={16} />}
        >
          {t("common:buttons.clear", { defaultValue: "Clear" })}
        </Button>
      )}
    </Group>
  );
};

export const RentalAssetsFilterBar = React.memo(RentalAssetsFilterBarComponent);
